import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, fmtINR, fmtDate } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { Plus, Trash, PencilSimple, X, FloppyDisk, Eye, Robot } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { sanitizeGstin } from "../lib/constants";
import { VoucherDetailModal } from "../components/VoucherDetailModal";
import { RescanBillModal } from "../components/RescanBillModal";

const ITC_TYPES = [
  { code: "OTH",  label: "All Other ITC (regular purchases)" },
  { code: "IMPG", label: "Import of Goods" },
  { code: "IMPS", label: "Import of Services" },
  { code: "ISRC", label: "Inward — Reverse Charge" },
  { code: "ISD",  label: "ISD (Input Service Distributor)" },
];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function emptyForm() {
  return {
    vendor_name: "",
    vendor_gstin: "",
    bill_no: "",
    bill_date: todayISO(),
    itc_type: "OTH",
    taxable: 0,
    igst: 0,
    cgst: 0,
    sgst: 0,
    cess: 0,
    eligible: true,
    notes: "",
    dr_ledger_id: "",
    cr_ledger_id: "",
    items: [],
  };
}
const emptyItem = () => ({ name: "", hsn: "", qty: 1, rate: 0, gst_rate: 18, unit: "" });
const itemAmount = (it) => Math.round(Number(it.qty || 0) * Number(it.rate || 0) * 100) / 100;


function BillItemsEditor({ items, onChange, onApplyTotals }) {
  const set = (i, patch) => onChange(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const total = items.reduce((s, it) => s + itemAmount(it), 0);
  return (
    <div className="md:col-span-2 border border-border rounded-md p-3 space-y-2" data-testid="bill-items-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-overline">Bill Items <span className="text-ink-muted font-normal normal-case tracking-normal">(edit names, qty, rates as per the vendor bill)</span></div>
        <div className="flex gap-2">
          <button type="button" onClick={() => onChange([...items, emptyItem()])} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid="bill-item-add"><Plus size={12} /> Add item</button>
          {items.length > 0 && <button type="button" onClick={onApplyTotals} className="btn-secondary text-xs" data-testid="bill-items-apply" title="Set taxable & GST from these items">Use items total ({fmtINR(total)})</button>}
        </div>
      </div>
      {items.length === 0 && <div className="text-xs text-ink-muted py-2">No items yet — add lines to record what was purchased.</div>}
      {items.length > 0 && (
        <table className="w-full text-xs">
          <thead><tr className="text-left uppercase text-ink-muted"><th className="py-1">Item</th><th>HSN</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">GST%</th><th className="text-right">Amount</th><th></th></tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} data-testid={`bill-item-row-${i}`}>
                <td className="py-1 pr-1"><input className="field" value={it.name} onChange={e => set(i, { name: e.target.value })} placeholder="Item name" data-testid={`bill-item-name-${i}`} /></td>
                <td className="py-1 pr-1"><input className="field w-20 font-mono" value={it.hsn || ""} onChange={e => set(i, { hsn: e.target.value })} data-testid={`bill-item-hsn-${i}`} /></td>
                <td className="py-1 pr-1"><input type="number" step="0.001" className="field w-20 text-right" value={it.qty} onChange={e => set(i, { qty: e.target.value })} data-testid={`bill-item-qty-${i}`} /></td>
                <td className="py-1 pr-1"><input type="number" step="0.01" className="field w-24 text-right" value={it.rate} onChange={e => set(i, { rate: e.target.value })} data-testid={`bill-item-rate-${i}`} /></td>
                <td className="py-1 pr-1"><input type="number" step="0.01" className="field w-16 text-right" value={it.gst_rate} onChange={e => set(i, { gst_rate: e.target.value })} data-testid={`bill-item-gst-${i}`} /></td>
                <td className="py-1 text-right tabular font-semibold whitespace-nowrap" data-testid={`bill-item-amount-${i}`}>{fmtINR(itemAmount(it))}</td>
                <td className="py-1 pl-1"><button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="btn-ghost text-destructive" data-testid={`bill-item-remove-${i}`}><Trash size={13} /></button></td>
              </tr>
            ))}
            <tr className="font-bold"><td colSpan={5} className="text-right pt-1">Items total</td><td className="text-right tabular pt-1" data-testid="bill-items-total">{fmtINR(total)}</td><td></td></tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function InwardBillsPage() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // bill object or null
  const [form, setForm] = useState(emptyForm());
  const [open, setOpen] = useState(false);
  const [ledgers, setLedgers] = useState([]);
  const [viewKey, setViewKey] = useState(null);
  const closeView = React.useCallback(() => setViewKey(null), []);
  const [rescanBill, setRescanBill] = useState(null);
  useEffect(() => { api.get("/accounting/ledgers").then(r => setLedgers(r.data)).catch((err) => console.error("Ledgers load failed:", err)); }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/inward-bills");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...emptyForm(), ...b, bill_date: String(b.bill_date || "").slice(0, 10), items: (b.items || []).map(it => ({ ...it, qty: it.qty ?? it.quantity ?? 0 })) }); setOpen(true); };

  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const editId = params.get("edit");
    if (!editId || rows.length === 0) return;
    const b = rows.find(r => r.id === editId);
    if (b) { setViewKey(null); openEdit(b); }
    else toast.error("Bill not found");
    setParams({}, { replace: true });
    // eslint-disable-next-line
  }, [rows, params]);

  const applyItemTotals = () => {
    const items = form.items || [];
    const taxable = items.reduce((s, it) => s + itemAmount(it), 0);
    const tax = items.reduce((s, it) => s + itemAmount(it) * Number(it.gst_rate || 0) / 100, 0);
    const inter = Number(form.igst || 0) > 0 && Number(form.cgst || 0) === 0;
    const r2 = (x) => Math.round(x * 100) / 100;
    setForm({ ...form, taxable: r2(taxable), igst: inter ? r2(tax) : 0, cgst: inter ? 0 : r2(tax / 2), sgst: inter ? 0 : r2(tax / 2) });
    toast.success("Taxable & GST updated from items");
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      taxable: Number(form.taxable || 0),
      igst: Number(form.igst || 0),
      cgst: Number(form.cgst || 0),
      sgst: Number(form.sgst || 0),
      cess: Number(form.cess || 0),
      eligible: !!form.eligible,
      items: (form.items || []).filter(it => (it.name || "").trim()).map(it => ({
        name: it.name.trim(), hsn: it.hsn || "", unit: it.unit || "", qty: Number(it.qty || 0), rate: Number(it.rate || 0),
        gst_rate: Number(it.gst_rate || 0), amount: itemAmount(it),
      })),
    };
    try {
      if (editing) await api.put(`/inward-bills/${editing.id}`, payload);
      else await api.post("/inward-bills", payload);
      toast.success(editing ? "Inward bill updated" : "Inward bill recorded");
      setOpen(false); setEditing(null);
      await load();
    } catch (e2) { toast.error(formatApiError(e2?.response?.data?.detail) || e2.message); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete inward bill ${b.bill_no}?`)) return;
    try { await api.delete(`/inward-bills/${b.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const totalsByType = rows.reduce((acc, r) => {
    const k = r.itc_type || "OTH";
    if (!acc[k]) acc[k] = { taxable: 0, igst: 0, cgst: 0, sgst: 0, cess: 0, count: 0 };
    acc[k].taxable += Number(r.taxable || 0);
    acc[k].igst += Number(r.igst || 0);
    acc[k].cgst += Number(r.cgst || 0);
    acc[k].sgst += Number(r.sgst || 0);
    acc[k].cess += Number(r.cess || 0);
    acc[k].count += 1;
    return acc;
  }, {});

  return (
    <div data-testid="inward-bills-page">
      <PageHeader
        title="Inward Bills"
        subtitle="Record purchase invoices to claim ITC. These flow into GSTR-3B Section 4(A) bucketed by ITC type."
        actions={<button onClick={openNew} className="btn-primary inline-flex items-center gap-1.5" data-testid="new-inward-bill"><Plus size={14} weight="bold" /> New Inward Bill</button>}
      />

      <div className="p-8 space-y-6">
        {/* Summary by ITC type */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {ITC_TYPES.map(t => {
            const v = totalsByType[t.code] || { taxable: 0, igst: 0, cgst: 0, sgst: 0, count: 0 };
            return (
              <div key={t.code} className="card-base p-4" data-testid={`itc-summary-${t.code}`}>
                <div className="flex items-center justify-between">
                  <div className="label-overline">{t.code}</div>
                  <div className="text-[10px] text-ink-muted">{v.count} bill{v.count !== 1 ? "s" : ""}</div>
                </div>
                <div className="mt-1 text-xs text-ink-muted">{t.label}</div>
                <div className="mt-2 text-sm font-semibold tabular">{fmtINR(v.taxable)}</div>
                <div className="text-[11px] text-ink-muted">IGST {fmtINR(v.igst)} · C {fmtINR(v.cgst)} · S {fmtINR(v.sgst)}</div>
              </div>
            );
          })}
        </div>

        {/* Table */}
        <div className="table-wrapper">
          <table className="table-base">
            <thead><tr>
              <th>Bill No</th><th>Date</th><th>Vendor</th><th>GSTIN</th><th>ITC Type</th>
              <th className="text-right">Taxable</th><th className="text-right">IGST</th><th className="text-right">CGST</th><th className="text-right">SGST</th>
              <th>Eligible</th><th className="text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={11} className="text-center py-10 text-ink-muted">No inward bills yet. Click <span className="font-semibold">New Inward Bill</span> to record your first purchase invoice.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} data-testid={`bill-row-${r.id}`} className="cursor-pointer hover:bg-surface-hover" onClick={() => setViewKey(`ib:${r.id}`)}>
                  <td className="font-mono font-semibold">{r.bill_no}{(r.items?.length > 0) && <div className="text-[10px] text-ink-muted font-sans font-normal">{r.items.length} item{r.items.length !== 1 ? "s" : ""}</div>}</td>
                  <td>{fmtDate(r.bill_date)}</td>
                  <td>{r.vendor_name}</td>
                  <td className="font-mono text-xs">{r.vendor_gstin || "—"}</td>
                  <td><span className="status-pill status-info">{r.itc_type}</span></td>
                  <td className="text-right tabular">{fmtINR(r.taxable)}</td>
                  <td className="text-right tabular">{fmtINR(r.igst)}</td>
                  <td className="text-right tabular">{fmtINR(r.cgst)}</td>
                  <td className="text-right tabular">{fmtINR(r.sgst)}</td>
                  <td>{r.eligible ? <span className="status-pill status-success">Yes</span> : <span className="status-pill status-error">No</span>}</td>
                  <td className="text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setViewKey(`ib:${r.id}`)} className="btn-ghost inline-flex items-center gap-1" data-testid={`view-bill-${r.id}`}><Eye size={14} /> View</button>
                    <button onClick={() => setRescanBill(r)} className="btn-ghost inline-flex items-center gap-1 text-brand" data-testid={`rescan-bill-${r.id}`} title="Re-read with AI"><Robot size={14} /> Re-read</button>
                    <button onClick={() => openEdit(r)} className="btn-ghost inline-flex items-center gap-1" data-testid={`edit-bill-${r.id}`}><PencilSimple size={14} /> Edit</button>
                    <button onClick={() => remove(r)} className="btn-ghost inline-flex items-center gap-1 text-destructive" data-testid={`delete-bill-${r.id}`}><Trash size={14} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <VoucherDetailModal voucherId={viewKey} onClose={closeView} />
      {rescanBill && <RescanBillModal bill={rescanBill} onClose={() => setRescanBill(null)} onApplied={() => { setRescanBill(null); load(); }} />}

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="bill-modal">
          <div className="bg-white rounded-lg w-full max-w-3xl shadow-2xl max-h-[94vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-white z-10">
              <div className="font-display font-semibold">{editing ? "Edit Inward Bill" : "New Inward Bill"}</div>
              <button onClick={() => setOpen(false)} className="btn-ghost" data-testid="close-modal"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="md:col-span-2">
                <div className="label-overline mb-1">Vendor Name <span className="text-destructive">*</span></div>
                <input className="field" required value={form.vendor_name} onChange={e => setForm({ ...form, vendor_name: e.target.value })} data-testid="bill-vendor-name" />
              </div>
              <div>
                <div className="label-overline mb-1">Vendor GSTIN</div>
                <input className="field font-mono uppercase" value={form.vendor_gstin} onChange={e => setForm({ ...form, vendor_gstin: sanitizeGstin(e.target.value) })} data-testid="bill-vendor-gstin" />
              </div>
              <div>
                <div className="label-overline mb-1">Bill Number <span className="text-destructive">*</span></div>
                <input className="field" required value={form.bill_no} onChange={e => setForm({ ...form, bill_no: e.target.value })} data-testid="bill-no" />
              </div>
              <div>
                <div className="label-overline mb-1">Bill Date <span className="text-destructive">*</span></div>
                <input type="date" className="field" required value={form.bill_date} onChange={e => setForm({ ...form, bill_date: e.target.value })} data-testid="bill-date" />
              </div>
              <div>
                <div className="label-overline mb-1">ITC Type</div>
                <select className="field" value={form.itc_type} onChange={e => setForm({ ...form, itc_type: e.target.value })} data-testid="bill-itc-type">
                  {ITC_TYPES.map(t => <option key={t.code} value={t.code}>{t.code} — {t.label}</option>)}
                </select>
              </div>
              <div>
                <div className="label-overline mb-1">Taxable Value (₹)</div>
                <input type="number" step="0.01" className="field text-right" value={form.taxable} onChange={e => setForm({ ...form, taxable: e.target.value })} data-testid="bill-taxable" />
              </div>
              <div>
                <div className="label-overline mb-1">IGST (₹)</div>
                <input type="number" step="0.01" className="field text-right" value={form.igst} onChange={e => setForm({ ...form, igst: e.target.value })} data-testid="bill-igst" />
              </div>
              <div>
                <div className="label-overline mb-1">CGST (₹)</div>
                <input type="number" step="0.01" className="field text-right" value={form.cgst} onChange={e => setForm({ ...form, cgst: e.target.value })} data-testid="bill-cgst" />
              </div>
              <div>
                <div className="label-overline mb-1">SGST (₹)</div>
                <input type="number" step="0.01" className="field text-right" value={form.sgst} onChange={e => setForm({ ...form, sgst: e.target.value })} data-testid="bill-sgst" />
              </div>
              <div>
                <div className="label-overline mb-1">Cess (₹)</div>
                <input type="number" step="0.01" className="field text-right" value={form.cess} onChange={e => setForm({ ...form, cess: e.target.value })} data-testid="bill-cess" />
              </div>
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={form.eligible} onChange={e => setForm({ ...form, eligible: e.target.checked })} data-testid="bill-eligible" />
                <span className="text-sm">ITC Eligible (counts toward GSTR-3B Section 4(A))</span>
              </label>
              <BillItemsEditor items={form.items || []} onChange={items => setForm({ ...form, items })} onApplyTotals={applyItemTotals} />
              <div className="md:col-span-2 border border-dashed border-border rounded-md p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="label-overline mb-1">Ledger to DEBIT (optional)</div>
                  <select className="field" value={form.dr_ledger_id || ""} onChange={e => setForm({ ...form, dr_ledger_id: e.target.value })} data-testid="bill-dr-ledger">
                    <option value="">Auto — Purchase A/c (Intra/Inter)</option>
                    {ledgers.map(l => <option key={l.id} value={l.id}>{l.name} ({l.group_name})</option>)}
                  </select>
                </div>
                <div>
                  <div className="label-overline mb-1">Ledger to CREDIT (optional)</div>
                  <select className="field" value={form.cr_ledger_id || ""} onChange={e => setForm({ ...form, cr_ledger_id: e.target.value })} data-testid="bill-cr-ledger">
                    <option value="">Auto — vendor ledger</option>
                    {ledgers.map(l => <option key={l.id} value={l.id}>{l.name} ({l.group_name})</option>)}
                  </select>
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="label-overline mb-1">Notes</div>
                <textarea rows={2} className="field" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex items-center justify-end gap-2 pt-2 border-t border-border">
                <button type="button" onClick={() => setOpen(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary inline-flex items-center gap-1.5" data-testid="save-bill"><FloppyDisk size={14} weight="bold" /> {editing ? "Save Changes" : "Save Bill"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <FieldStyle />
    </div>
  );
}
